import React, { useEffect, useState } from 'react';
import { EditProps, useNotify, useRedirect, useRefresh } from 'react-admin';
import { Link } from 'react-router-dom';
import { clsx } from 'clsx';
import { Button, Dialog, DialogActions, DialogTitle, Input } from '@mui/material';
import { AutoComplete, InputNumber, Modal, Select, Space, Upload } from 'antd';
import { RcFile } from 'antd/lib/upload';
import moment from 'moment-timezone';
import dayjs from 'dayjs';

import { axiosErrorToString, dateFormatter, moneyFormatter, rbac, showAxiosError } from '@utils';
import {
  CalculateInvoiceResult,
  creditHttp,
  http,
  ordersHttp,
  s3http,
  UpdateOrderParams,
} from '@network';
import { useRecord } from '@hooks';
import {
  Credit,
  CreditHistory,
  DealerType,
  Order,
  OrderCompositionProduct,
  OrderTransaction, ProductOriginType,
} from '@types';
import { InitialCard, InitialPage, LightTooltip, Loader, Scroll } from '@components';
import { OrderStatus } from '@pages/order/OrderStatus';
import {
  CreditInvoiceProducts,
} from '@pages/credit/CreditEdit/CreditInvoices/CreditInvoiceProducts';
import cls from './order-edit.module.css';
import * as zipCodes from '@utils/zip-codes.json';

const REQUEST_HOURS = 168;
const MAX_POSITIONS = 60;

export const OrderEdit = (props: EditProps) => {
  moment.locale('en');
  const order = useRecord<Order>(props);
  const redirect = useRedirect();
  const notify = useNotify();
  const refresh = useRefresh();

  const [loading, setLoading] = useState(false);
  const [composition, setComposition] = useState<OrderCompositionProduct[]>([]);
  const [invoiceTotal, setInvoiceTotal] = useState<number>(0);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [isQuotationModalOpen, setIsQuotationModalOpen] = useState<boolean>(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);
  const [creditsList, setCreditsList] = useState<Credit[]>([]);
  const [selectedCreditId, setSelectedCreditId] = useState<number | null>(null);
  const [requestHours, setRequestHours] = useState<number>(24);

  const [creditId, setCreditId] = useState<number | null>(order?.creditId || null);
  const [discountAmount, setDiscountAmount] = useState<number>(0);
  const [isrPercent, setIsrPercent] = useState<number | null>(null);
  const [iepsPercent, setIepsPercent] = useState<number | null>(null);
  const [calculate, setCalculate] = useState<CalculateInvoiceResult>();

  const [retailerName, setRetailerName] = useState<string | null>(order?.retailerName || null);
  const [zipState, setZipState] = useState<string | null>(order?.zipState || null);
  const [collectionCode, setCollectionCode]
    = useState<string | null>(order?.collectionCode || null);
  const [retailers, setRetailers] = useState<CheckOption[]>([]);
  const [states, setStates] = useState<CheckOption[]>([]);
  const [isUpdateQuotationProcess, setIsUpdateQuotationProcess] = useState(false);
  const [isUpdateBolProcess, setIsUpdateBolProcess] = useState(false);

  const backTo = '/order';

  const str = (s?: string | null) => `${s || ''}`.toLowerCase()
    .normalize('NFD').replace(/\p{Diacritic}/gu, '');
  const retailersOptions = retailers.filter(o => str(o.label).includes(str(retailerName)));
  const statesOptions = states.filter(s => str(s.label).includes(str(zipState ?? '')));

  const onCreditSelect = (creditId: number) => {
    setSelectedCreditId(creditId);
  };

  const changeHours = (num: any) => {
    setRequestHours(num);
  };

  const submit = async (params: SubmitOrderParams = {}) => {
    const { sendQuotation, updateQuotation, confirmOrder, cancelOrder, resetPayment } = params;
    const selectedComposition = composition.filter(c => c.productId);

    if (selectedComposition.length === 0) {
      return notify('Add at least one product', { type: 'error' });
    }

    setIsUpdateQuotationProcess(true);

    try {
      const params: UpdateOrderParams = {
        orderId: order.id,
        creditId: creditId,
        composition: selectedComposition,
        userId: order.userId,
        requestHours: REQUEST_HOURS,
        discountAmount,
        isrPercent,
        iepsPercent,
        retailerName,
        zipState,
        sendQuotation,
        updateQuotation,
        confirmOrder,
        cancelOrder,
        resetPayment,
        collectionCode,
      };

      await ordersHttp.updateOrder(params);
      refresh();

      const message = sendQuotation
        ? 'Order Quotation is sent'
        : resetPayment
          ? 'Quotation Expires changed'
          : updateQuotation
            ? 'Update Quotation changed'
            : confirmOrder
              ? 'Order is confirmed'
              : cancelOrder
                ? 'Order is canceled'
                : 'Order is updated';
      notify(message, { type: cancelOrder ? 'warning' : 'success' });
    } catch (e) {
      notify(axiosErrorToString(e));
    } finally {
      setIsUpdateQuotationProcess(false);
    }
  };

  const remove = async () => {
    try {
      await ordersHttp.removeOrders([order.id]);
      redirect(backTo);
    } catch (e) {
      notify(axiosErrorToString(e));
    }
  };

  const requestQuotation = () => {
    setIsQuotationModalOpen(false);

    submit();
  };

  const confirm = async () => {
    setIsConfirmModalOpen(false);

    if (selectedCreditId) {
      try {
        await creditHttp.confirmOrderQuotation({
          creditId: selectedCreditId, orderId: order.id,
        });
        redirect(backTo);
      } catch (e) {
        notify(axiosErrorToString(e));
      }
    }
  };

  const calculateRequest = async () => {
    if (composition.length === 0) return;

    try {
      const nextCalculate = await creditHttp.calculateInvoice({
        composition: composition.map(position => ({
          ...position,
          externalShop: '',
        })),
        discountAmount,
        isrPercent,
        iepsPercent,
      });
      setCalculate(nextCalculate);
    } catch (err: unknown) {
      console.error(err);
    }
  };

  const getStates = async () => {
    const nextStates: CheckOption[] = [];
    for (const [zip, state] of Object.entries(zipCodes)) {
      const zipCode = [zip, state].filter(Boolean).join(' - ');
      const option: CheckOption = {
        value: zipCode,
        label: zipCode,
      };
      nextStates.push(option);
    }
    setStates(nextStates);
  };

  const getRetailers = async () => {
    const response = await http.get<any>(`dealer/by-type/${DealerType.RETAILER}`, {
      params: { _start: 0, _end: 9999999 },
    });
    const nextOptions: CheckOption[] = [];
    if (response.data && Array.isArray(response.data)) {
      (response.data ?? []).forEach((model: any) => {
        nextOptions.push({ value: model['name'], label: model['name'] });
      });
    }
    setRetailers(nextOptions);
  };

  useEffect(() => {
    getStates();
    getRetailers();
  }, []);

  useEffect(() => void calculateRequest(), [
    composition, discountAmount, isrPercent, iepsPercent,
  ]);

  useEffect(() => {
    if (order) {
      const orderComposition: OrderCompositionProduct[] = [];

      order.composition.forEach((item) => {
        const compositionProduct =
          order.positions?.find(p => p.productId === item.productId)?.product;
        orderComposition.push({
          ...item,
          product: compositionProduct,
        });
      });
      setComposition(orderComposition);

      (async () => {
        const loadedCredits = await creditHttp.getCreditsByUser(order.userId);
        setCreditsList(loadedCredits);
      })();

      if (order.isrPercent) {
        setIsrPercent(order.isrPercent);
      }
      if (order.iepsPercent) {
        setIepsPercent(order.iepsPercent);
      }
      if (order.discountAmount) {
        setDiscountAmount(order.discountAmount);
      }
      if (order.status === 'paid') {
        setRequestHours(0);
      }

      setCreditId(order.creditId || null);
      setRetailerName(order.retailerName || null);
      setZipState(order.zipState || null);
      setCollectionCode(order.collectionCode || null);
    }
  }, [order]);

  if (!order) return null;

  const invoice = order.invoice;
  const isSuperadmin = rbac.isSuperadmin;
  const isDisabled = order.isCancelled || order.isConfirmed || order.isPaid;
  const isExternalOrder = !!order.partnerId;
  const deleteDisabled = !order.isWaiting || !isSuperadmin;
  const deleteTitle = !isSuperadmin
    ? 'Only Superadmin can delete orders'
    : !order.isWaiting ? 'Delete is allowed only for "Quotation in progress"' : undefined;
  const credit = creditsList.find(c => c.id === creditId) as Credit;

  const creditsListOptions = creditsList.map(c => ({
    value: c.id,
    label: `Credit # ${c.code} (${c.statusEn}). Balance: ${moneyFormatter.format(c.balance)}`,
  }));
  const creditsPickOptions = creditsList.map(c => ({
    value: c.id,
    label: `${c.code} | ${c.statusEn}. Balance: ${moneyFormatter.format(c.balance)}`,
  }));

  const downloadQuotation = async () => {
    await ordersHttp.downloadQuotationFile(order.id);
  };

  const downloadBol = async () => {
    if (loading) return;
    try {
      setLoading(true);
      await ordersHttp.downloadBolFile(order.id);
    } catch (err: any) {
      showAxiosError(err);
    } finally {
      setLoading(false);
    }
  };

  const onBolUpload = async (rcFile: RcFile) => {
    setIsUpdateBolProcess(true);

    try {
      const file = await s3http.uploadFinanceFile(rcFile);
      await ordersHttp.updateBolFile(order.id, file.id);
      refresh();

      return rcFile.uid;
    } catch (err: any) {
      showAxiosError(err);

      return '';
    } finally {
      setIsUpdateBolProcess(false);
    }
  };

  const showGenerateWithdrawal = order.withdrawals?.length === 0 && order.status === 'confirmed';
  const isBolFileDownloadDisabled = !order.bolFileId || isExternalOrder;

  return (
    <InitialPage title="Order" backTo={backTo} backText="Back to all orders">
      <div className={cls.wrap}>
        <div className={cls.card}>
          <InitialCard contentClass={cls.content}>
            <div className={cls.title}>Details</div>
            <div className={cls.cardInfo}>
              <div className={cls.icon}><DetailsIcon /></div>
              <div className={cls.cardValues}>
                <div className={cls.cardValuesRow}>
                  <div className={cls.cardLabel}>Status</div>
                  <div className={clsx(cls.cardValue, cls.cardValueShort)}>
                    <OrderStatus status={{
                      label: order.statusEn,
                      value: order.status,
                    }} />
                  </div>
                </div>
                <div className={cls.cardValuesRow}>
                  <div className={cls.cardLabel}>Created at</div>
                  <div className={cls.cardValue}>{dateFormatter.toDateSlash(order.createdAt)}</div>
                </div>
                <div className={cls.cardValuesRow}>
                  <div className={cls.cardLabel}>Sold at</div>
                  <div className={cls.cardValue}>
                    {order.paymentPaidAt
                      ? dateFormatter.toDateSlash(order.paymentPaidAt)
                      : ''
                    }
                  </div>
                </div>
                <div className={cls.cardValuesRow}>
                  <div className={cls.cardLabel}>Order #</div>
                  <div className={cls.cardValue}>{order.code}</div>
                </div>
                <div className={cls.cardValuesRow}>
                  <div className={cls.cardLabel}>User code</div>
                  <div className={cls.cardValue}>{order.user?.code || 'Unregistered'}</div>
                </div>
                <div className={cls.cardValuesRow}>
                  <div className={cls.cardLabel}>Total amount</div>
                  <div className={cls.cardValue}>
                    {moneyFormatter.format(order.needToPayAmount)}
                  </div>
                </div>
                <div className={cls.cardValuesRow}>
                  <div className={cls.cardLabel}>Payment type</div>
                  <div className={cls.cardValue}>
                    {moneyFormatter.format(order.isPaid ? order.needToPayAmount : 0)}
                  </div>
                </div>
              </div>
            </div>
            <div className={cls.cardFooter}>
              {invoice ? (
                <Link to={`/credit/${invoice.creditId}?invoices=${invoice.id}`}>
                  <Button variant="contained" className={cls.quotationSubmit}>
                    Go to Invoice Record
                  </Button>
                </Link>
              ) : (
                <Button variant="contained" className={cls.quotationSubmit} disabled>
                  Go to Invoice Record
                </Button>
              )}
            </div>
          </InitialCard>
        </div>
        <div className={cls.card}>
          <InitialCard contentClass={cls.content}>
            <div className={cls.title}>User</div>
            <div className={cls.cardInfo}>
              <div className={cls.icon}><UserIcon /></div>
              <div className={cls.cardValues}>
                <div className={cls.cardValuesRow}>
                  <div className={cls.cardLabel}>Full name</div>
                  <div className={cls.cardValue}>
                    {order.user ? (
                      <Link to={`/user/${order.userId}?initial`}>
                        {order.user?.fullName}
                      </Link>
                    ) : order.guestName}
                  </div>
                </div>
                <div className={cls.cardValuesRow}>
                  <div className={cls.cardLabel}>E-mail</div>
                  <div className={cls.cardValue}>{order.user?.email || order.guestEmail}</div>
                </div>
                <div className={cls.cardValuesRow}>
                  <div className={cls.cardLabel}>Phone</div>
                  <div className={cls.cardValue}>
                    {rbac.canShowUserPhone()
                      ? (order.user?.phone || order.guestPhone)
                      : '*'.repeat(`${order.user?.phone || order.guestPhone}`.length)}
                  </div>
                </div>
                <div className={cls.cardValuesRow}>
                  <div className={cls.cardLabel}>RFC</div>
                  <div className={cls.cardValue}>{order.user?.rfc || order.guestRfc}</div>
                </div>
                <div className={cls.cardValuesRow}>
                  <div className={cls.cardLabel}>User state</div>
                  <div className={cls.cardValue}>{order.user?.state?.name}</div>
                </div>
              </div>
            </div>
            <div className={cls.title}>External Shop</div>
            <div className={cls.cardInfo}>
              <div className={cls.icon}><ExternalShopIcon /></div>
              <div className={cls.cardValues}>
                <div className={cls.cardValuesRow}>
                  <div className={cls.cardLabel}>Retail Store</div>
                  <div className={cls.cardValue}>
                    <AutoComplete
                      className={cls.autocomplete}
                      onChange={val => setRetailerName(val)}
                      placeholder="Retail Store"
                      options={retailersOptions}
                      allowClear
                      value={retailerName}
                      disabled={isDisabled || isExternalOrder}
                    />
                  </div>
                </div>
                <div className={cls.cardValuesRow}>
                  <div className={cls.cardLabel}>ZIP Code</div>
                  <div className={cls.cardValue}>
                    <AutoComplete
                      className={cls.autocomplete}
                      onChange={val => setZipState(val)}
                      placeholder="ZIP Code"
                      options={statesOptions}
                      allowClear
                      value={zipState}
                      disabled={isDisabled || isExternalOrder}
                    />
                  </div>
                </div>
              </div>
            </div>
          </InitialCard>
        </div>
        <div className={clsx(cls.card, cls.fullWidth)}>
          <InitialCard contentClass={clsx(cls.content, cls.quotationCard)}>
            <div className={cls.quotationActions}>
              {/* Confirmed status */}
              {order.isConfirmed && (
                <div className={cls.quotationRow}>
                  <div>Collection 6 Digit Code:</div>
                  <Input
                    name="collectionCode"
                    placeholder="Collection 6 Digit Code"
                    value={collectionCode || undefined}
                    onChange={e => setCollectionCode(e.target.value || null)}
                    inputProps={{
                      maxLength: 6,
                    }}
                    onBeforeInputCapture={(e: any) => {
                      const allowedChars = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
                      if (!allowedChars.includes(`${e.data}`)) e.preventDefault();
                    }}
                  />
                </div>
              )}

              {/* Quotation is Ready (price_request) */}
              {order.isRequestPrice && (
                <>
                  <div className={cls.quotationColumn}>
                    <Button variant="outlined" color="secondary" className={cls.btnIcon}
                      onClick={() => submit({ resetPayment: true })}>
                      <LastUpdateIcon />
                    </Button>
                    <div>Quotation Expires: {order.paymentExpiresAt ?
                      dateFormatter.toDateSlash(order.paymentExpiresAt) : '—'}
                    </div>
                  </div>
                  <Button
                    variant="contained"
                    color="secondary"
                    className={cls.btnIcon}
                    onClick={downloadQuotation}
                    disabled={!order.quotationUpdateFileId}
                  >
                    <DownloadIcon />
                  </Button>
                  <div className={cls.quotationColumn}>
                    <Button
                      variant="outlined"
                      color="primary"
                      className={cls.btnGreenBordered}
                      onClick={() => submit({ updateQuotation: true })}
                      disabled={isUpdateQuotationProcess}
                    >
                      {isUpdateQuotationProcess ? 'Loading...' : 'Update Quotation'}
                    </Button>
                    <div>Last Update: {order.quotationUpdateAt ?
                      dateFormatter.toDateSlash(order.quotationUpdateAt) : '—'}
                    </div>
                  </div>
                  <Button variant="contained" color="primary"
                    className={cls.btnGreen}
                    onClick={() => submit({ confirmOrder: true })}>
                    Confirm Order
                  </Button>
                </>
              )}

              {/* Waiting status (default) */}
              {order.isWaiting && (
                <Button variant="contained" color="primary"
                  className={cls.btnGreen}
                  onClick={() => submit({ sendQuotation: true })}
                  disabled={isExternalOrder}
                >
                  Send Quotation
                </Button>
              )}
            </div>
            <div className={cls.title}>Order summary</div>
            <div className={cls.composition}>
              <CreditInvoiceProducts
                discountAmount={discountAmount}
                setDiscountAmount={setDiscountAmount}
                isrPercent={isrPercent}
                setIsrPercent={setIsrPercent}
                iepsPercent={iepsPercent}
                setIepsPercent={setIepsPercent}
                calculate={calculate}
                composition={composition}
                setComposition={setComposition}
                externalShop={''}
                disabled={isDisabled || isExternalOrder}
                max={MAX_POSITIONS}
                origin={ProductOriginType.ORDER}
              />
            </div>
          </InitialCard>
        </div>
        <div className={cls.card}>
          <InitialCard contentClass={cls.content}>
            <div className={cls.title}>Transactions</div>
            {showGenerateWithdrawal && (
              <div className={cls.transactionActions}>
                <Button
                  className={cls.generateWithdrawal}
                  variant="contained"
                  onClick={() => redirect(`/finance-withdrawal/create?orderId=${order.id}`)}
                >Generate Withdrawal Record</Button>
              </div>
            )}
            <div className={cls.transactions}>
              {order.withdrawals?.map(withdrawal => {
                const date = moment(withdrawal.createdAt).format('DD MMM YYYY, HH:mm');

                return (
                  <div key={withdrawal.id} className={cls.historyRow}>
                    <div className={cls.transactionsContent}>
                      <div className={cls.transactionsLabel}>
                        <Link to={`/finance-withdrawal/${withdrawal.id}`}>{withdrawal.code}</Link>
                      </div>
                      <div className={cls.transactionsDate}>{date}</div>
                    </div>
                    <div className={cls.transactionsSummary}>
                      <div className={cls.transactionsAmount}>
                        - {moneyFormatter.format(withdrawal.amount)}
                      </div>
                    </div>
                  </div>
                );
              })}
              {order.transactions?.map((transaction: OrderTransaction) => {
                const date = moment(transaction.createdAt).format('DD MMM YYYY, HH:mm');

                return (
                  <div key={transaction.id} className={cls.historyRow}>
                    <div className={cls.transactionsContent}>
                      <div className={cls.transactionsLabel}>{transaction.title}</div>
                      <div className={cls.transactionsDate}>{date}</div>
                    </div>
                    <div className={cls.transactionsSummary}>
                      <div className={cls.transactionsAmount}>
                        {transaction.type === 'income' ? '+' : '-'} {moneyFormatter.format(transaction.amount)}
                      </div>
                      <div className={cls.transactionsDiff}>
                        {moneyFormatter.format(transaction.balanceBefore)}
                        <span>{'->'}</span>
                        {moneyFormatter.format(transaction.balanceAfter)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className={cls.bol}>
              <div className={cls.bolInfo}>
                <div className={cls.bolInfoHead}>
                  <div className={cls.bolName}>
                    BOL
                  </div>
                  <button
                    className={cls.bolAction}
                    onClick={downloadBol}
                    disabled={loading || isExternalOrder}
                  >
                    <DownloadIcon />
                  </button>
                </div>
                <div className={clsx(cls.bolComment, isBolFileDownloadDisabled && cls.disabled)}>
                  <div>Generated on:</div>
                  <div>
                    {order.bolCreatedAt ? dayjs(order.bolCreatedAt).format('DD/MM/YYYY') : 'DD/MM/YYYY'},
                    by {order.bolAdmin ? order.bolAdmin.username : '@AdminUser'}
                  </div>
                </div>
              </div>
              <div className={cls.bolFiles}>
                {order.bolFileId && order.status === 'paid' ? (
                  <div className={cls.bolFile} onClick={downloadBol}>
                    <svg xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="20"
                      viewBox="0 0 18 20"
                      fill="none">
                      <g clipPath="url(#clip0_4609_21744)">
                        <path d="M13.9203 3.25775H17.3962C17.3526 3.18441 17.2946 3.11816 17.2256 3.06191L13.6639 0.191081C13.5676 0.112747 13.4512 0.0556641 13.3242 0.0244141L13.3856 2.79775C13.3908 3.05191 13.628 3.25608 13.9203 3.25775Z"
                          fill="#042E6B" />
                        <path d="M13.9198 3.9475C13.1972 3.94375 12.6093 3.44083 12.5925 2.81292L12.5302 0H1.78394C0.797334 0.0025 -0.000957471 0.699167 8.61942e-07 1.55708V18.4429C-0.000957471 19.3008 0.797334 19.9975 1.78394 20H15.7056C16.6923 19.9975 17.4905 19.3008 17.4896 18.4429V3.9475H13.9198Z"
                          fill="#042E6B" />
                        <path d="M11.9439 12.1964L9.84462 14.0164C9.60791 14.2214 9.29406 14.3443 8.96056 14.3627C8.93133 14.366 8.9021 14.3677 8.87287 14.3681C8.84748 14.3693 8.82208 14.3693 8.79669 14.3681C8.46271 14.3552 8.14598 14.2368 7.90496 14.0356L5.77075 12.3764C5.47127 12.1448 5.44444 11.746 5.71085 11.4856C5.97727 11.2252 6.43583 11.2018 6.73531 11.4335L8.15508 12.5314V7.29393C8.14118 6.9456 8.45456 6.6531 8.85562 6.64101C9.25621 6.62893 9.59258 6.90143 9.60648 7.25018C9.60696 7.26476 9.60696 7.27935 9.60648 7.29393V12.4443L10.9218 11.3006C11.2064 11.0552 11.6659 11.0564 11.9482 11.3039C12.2304 11.5514 12.2285 11.951 11.9443 12.1964H11.9439Z"
                          fill="#E1E5ED" />
                      </g>
                      <defs>
                        <clipPath id="clip0_4609_21744">
                          <rect width="17.4896" height="20" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  </div>
                ) : (
                  <Upload
                    listType="picture-card"
                    showUploadList={false}
                    beforeUpload={(file: RcFile) => {
                      const isPDF = file.type === 'application/pdf';
                      if (!isPDF) {
                        notify('Only PDF files are allowed!', { type: 'error' });
                      }
                      return isPDF || Upload.LIST_IGNORE;
                    }}
                    maxCount={1}
                    className={clsx(cls.bolFile, isExternalOrder && cls.disabled)}
                    customRequest={async ({ onSuccess }) => setTimeout(() => {
                      onSuccess && onSuccess('ok');
                    }, 0)}
                    action={onBolUpload}
                    accept="application/pdf"
                    disabled={isUpdateBolProcess || isExternalOrder}
                  >
                    {isUpdateBolProcess ? (
                      <Loader mini />
                    ) : (
                      <span className={cls.uploadIcon}>+</span>
                    )}
                  </Upload>
                )}
              </div>
              <div className={cls.bolDumb}></div>
            </div>
          </InitialCard>
        </div>
        <div className={cls.card}>
          <InitialCard contentClass={cls.content}>
            <div className={cls.title}>History</div>
            <Scroll height={250}>
              <div className={cls.history}>
                {order.history?.map((historyRow: CreditHistory) => {
                  const date = moment(historyRow.createdAt).format('DD MMM YYYY, HH:mm');
                  return (
                    <div key={historyRow.id} className={cls.historyRow}>
                      <div className={cls.historyContent}>
                        <div className={cls.historyLabel}>{historyRow.title}</div>
                        <div className={cls.historyDate}>{date}</div>
                      </div>
                      {historyRow.statusBefore || historyRow.statusAfter ? (
                        <div className={cls.historyDiff}>
                          {historyRow.statusBefore}
                          {(historyRow.statusBefore && historyRow.statusAfter) && <span>→</span>}
                          {historyRow.statusAfter}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </Scroll>
          </InitialCard>
        </div>

        <div className={cls.actions}>
          {/* Delete Button */}
          <div>
            {deleteDisabled ? (
              <LightTooltip title={deleteTitle}>
                <div className={cls.actionDelete}>Delete Order</div>
              </LightTooltip>
            ) : (
              <Button variant="contained"
                className={cls.actionDelete}
                onClick={() => setIsDialogOpen(true)}
                disabled={deleteDisabled || isExternalOrder}
                title={deleteTitle}
              >Delete Order</Button>
            )}
          </div>

          <div>
            <Button variant="contained"
              className={cls.actionCancel}
              onClick={() => submit({ cancelOrder: true })}
              disabled={isExternalOrder}
            >Cancel Order</Button>
            <Button
              variant="contained"
              className={cls.actionSubmit}
              onClick={() => submit()}
              disabled={isExternalOrder}
            ><SaveIcon /> Save Order</Button>
          </div>
        </div>

        <Dialog
          open={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            Are you sure?
          </DialogTitle>
          <DialogActions>
            <Button onClick={() => setIsDialogOpen(false)}>
              No
            </Button>
            <Button onClick={remove} autoFocus>
              Yes
            </Button>
          </DialogActions>
        </Dialog>
      </div>

      <Modal
        title="Request Quotation"
        open={isQuotationModalOpen}
        onOk={requestQuotation}
        onCancel={() => setIsQuotationModalOpen(false)}
        className={cls.modal}
        centered
        okText="Yes"
        cancelText="No"
        okButtonProps={{}}
      >
        <div className={cls.modalContent}>
          <Space size={24}>
            <div>Quotation prices available hours:</div>
            <InputNumber
              defaultValue={requestHours}
              className={cls.hoursInput}
              onChange={changeHours}
            />
          </Space>
          <div>
            {'Do you really want to save product prices and make a request for a quotation confirmation?'}
          </div>
        </div>
      </Modal>

      <Modal
        title="Confirm order"
        open={isConfirmModalOpen}
        onOk={confirm}
        onCancel={() => setIsConfirmModalOpen(false)}
        className={cls.modal}
        centered
        okText="Yes"
        cancelText="No"
      >
        <div className={cls.modalContent}>
          <Space size={24}>
            <div className={cls.modalSummary}>Order total:</div>
            <div className={cls.modalSummary}>{moneyFormatter.format(invoiceTotal)}</div>
          </Space>

          <div className={cls.modalSelect}>
            <Select
              options={creditsListOptions}
              placeholder="Select a credit"
              onChange={(creditId) => onCreditSelect(+creditId)}
            />
          </div>
        </div>
      </Modal>
    </InitialPage>
  );
};

interface CheckOption {
  value: string,
  label: string,
}

interface SubmitOrderParams {
  sendQuotation?: boolean;
  updateQuotation?: boolean;
  confirmOrder?: boolean;
  cancelOrder?: boolean;
  resetPayment?: boolean;
}

const DetailsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg"
    width="25"
    height="24"
    viewBox="0 0 25 24"
    fill="none">
    <g clipPath="url(#clip0_4_249)">
      <path d="M13.3301 6.70901H21.3302C21.9549 6.70901 21.9556 7.63585 21.3302 7.63585H13.3301C12.7054 7.63585 12.7047 6.70901 13.3301 6.70901Z"
        fill="white" />
      <path d="M21.3302 10.1185H13.3301C12.7054 10.1185 12.7047 11.0454 13.3301 11.0454H21.3302C21.9549 11.0454 21.9556 10.1185 21.3302 10.1185Z"
        fill="white" />
      <path d="M21.3302 13.5287H13.3301C12.7054 13.5287 12.7047 14.4555 13.3301 14.4555H21.3302C21.9549 14.4555 21.9556 13.5287 21.3302 13.5287Z"
        fill="white" />
      <path d="M9.05571 7.63585H11.0277C11.6524 7.63585 11.653 6.70901 11.0277 6.70901H9.05571C8.43096 6.70901 8.43033 7.63585 9.05571 7.63585Z"
        fill="white" />
      <path d="M9.05571 11.0089H11.0277C11.6524 11.0089 11.653 10.0821 11.0277 10.0821H9.05571C8.43096 10.0821 8.43033 11.0089 9.05571 11.0089Z"
        fill="white" />
      <path d="M9.05571 14.382H11.0277C11.6524 14.382 11.653 13.4551 11.0277 13.4551H9.05571C8.43096 13.4551 8.43033 14.382 9.05571 14.382Z"
        fill="white" />
      <path d="M13.33 16.8282H9.05571C8.43096 16.8282 8.43033 17.755 9.05571 17.755H13.33C13.9547 17.755 13.9553 16.8282 13.33 16.8282Z"
        fill="white" />
      <path d="M9.05571 4.22636H15.6323C16.257 4.22636 16.2576 3.29953 15.6323 3.29953H9.05571C8.43096 3.29953 8.43033 4.22636 9.05571 4.22636Z"
        fill="white" />
      <path d="M24.9905 15.4565V8.32666H24.995V1.97477C24.995 0.886051 24.0909 0 22.9801 0H7.40619C6.29539 0 5.39137 0.886051 5.39137 1.97477V11.3086H2.01483C0.904025 11.3086 0 12.1946 0 13.2834V20.8951C0 22.6233 1.4298 23.9994 3.16724 24C3.0897 24 12.4836 24 15.3943 24H22.2829V23.9944H23.8413C24.4799 23.9944 25 23.4847 25 22.8588V17.0846V15.4565H24.9905ZM24.0304 16.6916V22.0197C24.0304 22.5974 23.5507 23.0676 22.9612 23.0676H16.1199C16.1199 23.0676 16.1199 23.0713 16.1199 23.0732H5.42478C5.98838 22.5121 6.337 21.7429 6.337 20.8951V7.30282V1.97477C6.337 1.39704 6.81675 0.926832 7.40619 0.926832H22.9801C23.5696 0.926832 24.0493 1.39704 24.0493 1.97477V15.4565H24.0304V16.6916Z"
        fill="white" />
    </g>
    <defs>
      <clipPath id="clip0_4_249">
        <rect width="25" height="24" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const UserIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg"
    width="25"
    height="24"
    viewBox="0 0 25 24"
    fill="none">
    <g clipPath="url(#clip0_4_249)">
      <path d="M13.3301 6.70901H21.3302C21.9549 6.70901 21.9556 7.63585 21.3302 7.63585H13.3301C12.7054 7.63585 12.7047 6.70901 13.3301 6.70901Z"
        fill="white" />
      <path d="M21.3302 10.1185H13.3301C12.7054 10.1185 12.7047 11.0454 13.3301 11.0454H21.3302C21.9549 11.0454 21.9556 10.1185 21.3302 10.1185Z"
        fill="white" />
      <path d="M21.3302 13.5287H13.3301C12.7054 13.5287 12.7047 14.4555 13.3301 14.4555H21.3302C21.9549 14.4555 21.9556 13.5287 21.3302 13.5287Z"
        fill="white" />
      <path d="M9.05571 7.63585H11.0277C11.6524 7.63585 11.653 6.70901 11.0277 6.70901H9.05571C8.43096 6.70901 8.43033 7.63585 9.05571 7.63585Z"
        fill="white" />
      <path d="M9.05571 11.0089H11.0277C11.6524 11.0089 11.653 10.0821 11.0277 10.0821H9.05571C8.43096 10.0821 8.43033 11.0089 9.05571 11.0089Z"
        fill="white" />
      <path d="M9.05571 14.382H11.0277C11.6524 14.382 11.653 13.4551 11.0277 13.4551H9.05571C8.43096 13.4551 8.43033 14.382 9.05571 14.382Z"
        fill="white" />
      <path d="M13.33 16.8282H9.05571C8.43096 16.8282 8.43033 17.755 9.05571 17.755H13.33C13.9547 17.755 13.9553 16.8282 13.33 16.8282Z"
        fill="white" />
      <path d="M9.05571 4.22636H15.6323C16.257 4.22636 16.2576 3.29953 15.6323 3.29953H9.05571C8.43096 3.29953 8.43033 4.22636 9.05571 4.22636Z"
        fill="white" />
      <path d="M24.9905 15.4565V8.32666H24.995V1.97477C24.995 0.886051 24.0909 0 22.9801 0H7.40619C6.29539 0 5.39137 0.886051 5.39137 1.97477V11.3086H2.01483C0.904025 11.3086 0 12.1946 0 13.2834V20.8951C0 22.6233 1.4298 23.9994 3.16724 24C3.0897 24 12.4836 24 15.3943 24H22.2829V23.9944H23.8413C24.4799 23.9944 25 23.4847 25 22.8588V17.0846V15.4565H24.9905ZM24.0304 16.6916V22.0197C24.0304 22.5974 23.5507 23.0676 22.9612 23.0676H16.1199C16.1199 23.0676 16.1199 23.0713 16.1199 23.0732H5.42478C5.98838 22.5121 6.337 21.7429 6.337 20.8951V7.30282V1.97477C6.337 1.39704 6.81675 0.926832 7.40619 0.926832H22.9801C23.5696 0.926832 24.0493 1.39704 24.0493 1.97477V15.4565H24.0304V16.6916Z"
        fill="white" />
    </g>
    <defs>
      <clipPath id="clip0_4_249">
        <rect width="25" height="24" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const ExternalShopIcon = () => (
  <svg width="55"
    height="55"
    viewBox="0 0 55 55"
    fill="none"
    xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_4853_21182)">
      <path d="M27.5 55C42.6878 55 55 42.6878 55 27.5C55 12.3122 42.6878 0 27.5 0C12.3122 0 0 12.3122 0 27.5C0 42.6878 12.3122 55 27.5 55Z"
        fill="var(--primary-color)" />
      <path d="M30.9978 20.6568C29.973 20.6398 28.9736 20.9955 28.186 21.6477C27.5254 22.4353 27.1697 23.4347 27.1866 24.4595C28.2114 24.4764 29.2108 24.1207 29.9984 23.4686C30.659 22.6809 31.0148 21.6816 30.9978 20.6568Z"
        fill="white" />
      <path d="M26.001 24.4595C25.9756 23.9598 25.7892 23.4856 25.4674 23.1044C25.0863 22.7826 24.612 22.5878 24.1123 22.5709C24.1292 23.0621 24.3156 23.5448 24.6289 23.9175C25.0101 24.2478 25.4928 24.4426 26.001 24.4595Z"
        fill="white" />
      <path d="M32.4631 29.033H22.5878C22.3168 29.033 22.0966 29.2532 22.0966 29.5242V33.4709C22.0966 33.7419 22.3168 33.9621 22.5878 33.9621H32.4631C32.7341 33.9621 32.9543 33.7419 32.9543 33.4709V29.5242C32.9543 29.2532 32.7341 29.033 32.4631 29.033ZM23.5787 30.0239H26.543C26.814 30.0239 27.0342 30.2441 27.0342 30.5151C27.0342 30.7861 26.814 31.0063 26.543 31.0063H23.5787C23.3077 31.0063 23.0875 30.7861 23.0875 30.5151C23.0875 30.2441 23.3077 30.0239 23.5787 30.0239ZM24.5696 32.9881H23.5787C23.3077 32.9881 23.0875 32.7679 23.0875 32.4969C23.0875 32.2259 23.3077 32.0057 23.5787 32.0057H24.5696C24.8406 32.0057 25.0608 32.2259 25.0608 32.4969C25.0608 32.7679 24.8406 32.9881 24.5696 32.9881ZM31.4806 32.9881H26.543C26.2719 32.9881 26.0517 32.7679 26.0517 32.4969C26.0517 32.2259 26.2719 32.0057 26.543 32.0057H31.4806C31.7516 32.0057 31.9718 32.2259 31.9718 32.4969C31.9718 32.7679 31.7516 32.9881 31.4806 32.9881ZM31.4806 31.0148H28.5163C28.2453 31.0148 28.0251 30.7946 28.0251 30.5236C28.0251 30.2525 28.2453 30.0323 28.5163 30.0323H31.4806C31.7516 30.0323 31.9718 30.2525 31.9718 30.5236C31.9718 30.7946 31.7516 31.0148 31.4806 31.0148Z"
        fill="white" />
      <path d="M17.1759 36.9094C15.7784 30.7183 15.7784 24.2901 17.1759 18.0905C17.142 18.0482 17.1081 18.0058 17.0742 17.9635C16.1765 18.1075 15.2957 18.3107 14.4233 18.5648C14.2201 18.6241 13.9999 18.5648 13.8474 18.4124C13.8474 18.4124 13.8051 18.37 13.8051 18.3616C12.8734 17.4299 11.9164 17.2013 11.7216 17.3961C11.5268 17.5908 11.7555 18.5479 12.6871 19.4795L12.7379 19.5219C12.8819 19.6574 12.9412 19.8691 12.8989 20.0639C12.2891 22.6809 12.0011 25.3657 12.035 28.0505C12.1366 30.0239 12.45 33.1321 12.8989 35.0547C12.9412 35.2495 12.8819 35.4527 12.7379 35.5967C12.7379 35.5967 12.6956 35.639 12.6871 35.6475C11.7555 36.5707 11.5353 37.5362 11.7216 37.731C11.7978 37.8072 12.035 37.8241 12.3822 37.714C12.9243 37.5108 13.407 37.1889 13.8051 36.7739L13.8474 36.7231C13.9914 36.5707 14.2116 36.5114 14.4064 36.5707C15.2618 36.7993 16.1257 36.9857 17.0065 37.1127C17.0658 37.045 17.1166 36.9772 17.1759 36.9094Z"
        fill="white" />
      <path d="M36.8586 37.5023C36.7146 37.3668 36.6554 37.1551 36.6977 36.9603C38.1968 30.7438 38.1968 24.2563 36.6977 18.0397C36.6469 17.8449 36.7062 17.6332 36.8586 17.4892L36.9094 17.4384C37.4515 16.9218 37.8749 16.2866 38.1375 15.5836C38.2899 15.1348 38.2815 14.7536 38.129 14.6012C37.7987 14.2709 36.5283 14.5842 35.3003 15.8123L35.241 15.8716C35.0885 16.024 34.8683 16.0833 34.6651 16.024C30.0154 14.5842 25.0439 14.5842 20.3857 16.024C20.1825 16.0833 19.9538 16.024 19.8098 15.8716C19.8098 15.8716 19.7675 15.8292 19.759 15.8208C19.1323 15.0924 18.2684 14.6181 17.3198 14.4741C17.1758 14.4657 17.0319 14.508 16.9218 14.5927C16.7693 14.7452 16.7608 15.1263 16.9133 15.5752C17.1758 16.2781 17.5993 16.9049 18.1329 17.4215L18.1922 17.4808C18.3361 17.6163 18.3954 17.828 18.3531 18.0228C16.9048 24.2478 16.9048 30.7268 18.3531 36.9518C18.3954 37.1466 18.3361 37.3499 18.1922 37.4939C18.1922 37.4939 18.1498 37.5362 18.1413 37.5447C16.8964 38.7897 16.5915 40.0516 16.9218 40.3819C17.2521 40.7122 18.5225 40.3988 19.7505 39.1708L19.8098 39.1115C19.9538 38.9591 20.174 38.8998 20.3688 38.9591C25.0523 40.2549 29.9984 40.2549 34.6735 38.9591C34.8768 38.9082 35.0885 38.9591 35.2325 39.1115L35.2833 39.1623C36.5283 40.4073 37.7902 40.7122 38.1206 40.3819C38.4509 40.0516 38.1375 38.7812 36.9094 37.5531L36.8502 37.4939L36.8586 37.5023ZM22.9435 21.9441C22.9689 21.6646 23.1891 21.4444 23.4601 21.4275C23.5363 21.4275 25.2471 21.2666 26.238 22.2914C26.2889 22.3422 26.3312 22.393 26.382 22.4523C26.5853 21.851 26.9156 21.3089 27.356 20.8516C28.889 19.3186 31.489 19.5388 31.5907 19.5388C31.8702 19.5642 32.0904 19.7844 32.1073 20.0555C32.1073 20.1656 32.3445 22.7572 30.803 24.2986C29.6173 25.4843 27.7964 25.6283 26.9834 25.6283H26.3397C25.4165 25.6622 24.5103 25.3488 23.8073 24.739C22.7826 23.7227 22.9265 22.0203 22.9265 21.9441H22.9435ZM33.9536 33.9706C33.9536 34.5126 33.5132 34.9615 32.9627 34.9615H22.105C21.563 34.9615 21.1141 34.5211 21.1141 33.9706V29.033C21.1141 28.4909 21.5545 28.0421 22.105 28.0421H32.9627C33.5048 28.0421 33.9536 28.4825 33.9536 29.033V33.9706Z"
        fill="white" />
      <path d="M42.262 35.6051C42.1181 35.4696 42.0588 35.2664 42.1096 35.0716C42.821 32.048 43.109 28.9398 42.965 25.84C42.8295 24.2901 42.4907 21.5968 42.1181 20.0893C42.0672 19.8945 42.1265 19.6828 42.2705 19.5472C42.279 19.5388 42.3128 19.5049 42.3213 19.4964C43.2529 18.5648 43.4732 17.6078 43.2868 17.413C43.2106 17.3367 42.9735 17.3198 42.6262 17.4299C42.0842 17.6332 41.6014 17.955 41.2034 18.37L41.1525 18.4208C41.0001 18.5733 40.7799 18.6241 40.5766 18.5648C39.7297 18.3107 38.8573 18.1075 37.985 17.9635C37.9511 17.9974 37.9257 18.0397 37.8918 18.0736C39.3316 24.2647 39.3316 30.7014 37.8918 36.8925C37.9511 36.9518 37.9935 37.0195 38.0528 37.0788C38.9082 36.9518 39.7636 36.7739 40.602 36.5452C40.8053 36.486 41.017 36.5452 41.161 36.6977L41.2034 36.7485C42.135 37.6801 43.092 37.9003 43.2868 37.714C43.4816 37.5277 43.2529 36.5622 42.3213 35.6306L42.2705 35.5882L42.262 35.6051Z"
        fill="white" />
    </g>
    <defs>
      <clipPath id="clip0_4853_21182">
        <rect width="55" height="55" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const LastUpdateIcon = () => (
  <svg width="55" height="38" viewBox="0 0 55 38" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="55" height="38" rx="8" fill="var(--primary-color)" />
    <path fillRule="evenodd"
      clipRule="evenodd"
      d="M28.7299 10.5986C33.4586 10.5986 37.292 14.3596 37.292 18.9991C37.292 23.6386 33.4586 27.3996 28.7299 27.3996C24.0012 27.3996 20.1678 23.6386 20.1678 18.9991C20.1678 14.3596 24.0012 10.5986 28.7299 10.5986ZM28.7299 11.7747C28.3989 11.7747 28.1306 12.0379 28.1306 12.3627V19.3351C28.1306 19.4265 28.1518 19.513 28.1896 19.5901C28.2083 19.6281 28.2312 19.6637 28.2574 19.6966C28.2726 19.7157 28.2887 19.7339 28.3061 19.751C28.3165 19.7612 28.3275 19.7709 28.3386 19.7803C28.3855 19.8201 28.4387 19.853 28.4967 19.877C28.5056 19.8807 28.5146 19.8842 28.5238 19.8875C28.5788 19.9073 28.6377 19.9194 28.699 19.9224L28.7299 19.9232H35.922C36.253 19.9232 36.5214 19.6599 36.5214 19.3351C36.5214 19.0104 36.253 18.7471 35.922 18.7471H29.3292V12.3627C29.3292 12.038 29.0609 11.7747 28.7299 11.7747Z"
      fill="white" />
    <path d="M14.6881 18.9991C14.562 18.9991 14.4596 19.0994 14.4616 19.2231C14.5055 21.914 15.339 24.5373 16.8647 26.7776C18.4328 29.08 20.6614 30.8745 23.269 31.9342C25.8765 32.9939 28.7457 33.2712 31.5139 32.731C34.282 32.1907 36.8247 30.8573 38.8204 28.8992C40.8161 26.9412 42.1752 24.4465 42.7258 21.7306C43.2764 19.0146 42.9938 16.1995 41.9137 13.6412C40.8337 11.0829 39.0046 8.89625 36.6579 7.35781C34.3746 5.86089 31.7009 5.04308 28.9582 5.00003C28.8322 4.99805 28.7299 5.09853 28.7299 5.22225V6.17431C28.7299 6.29803 28.8321 6.39812 28.9582 6.40032C31.4186 6.44322 33.8164 7.17886 35.8651 8.52194C37.9772 9.90653 39.6233 11.8745 40.5954 14.177C41.5674 16.4795 41.8218 19.0131 41.3262 21.4574C40.8307 23.9017 39.6075 26.147 37.8113 27.9092C36.0152 29.6715 33.7268 30.8716 31.2355 31.3578C28.7441 31.844 26.1618 31.5945 23.8151 30.6407C21.4683 29.687 19.4625 28.0719 18.0513 25.9997C16.6824 23.9897 15.9326 21.6371 15.8888 19.2231C15.8866 19.0994 15.7846 18.9991 15.6585 18.9991L14.6881 18.9991Z"
      fill="white" />
    <path d="M16.0384 13.7004C16.1509 13.5678 16.3674 13.6045 16.4281 13.7663L18.5808 19.5008C18.6416 19.6626 18.501 19.8282 18.3278 19.7989L12.1898 18.7608C12.0166 18.7315 11.9406 18.5292 12.0531 18.3967L16.0384 13.7004Z"
      fill="white" />
  </svg>
);

const DownloadIcon = () => (
  <svg width="63" height="46" viewBox="0 0 63 46" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g>
      <path d="M4 9.5C4 4.2533 8.27796 0 13.5551 0H49.4449C54.722 0 59 4.2533 59 9.5V28.5C59 33.7467 54.722 38 49.4449 38H13.5551C8.27795 38 4 33.7467 4 28.5V9.5Z"
        fill="var(--primary-color)" />
      <path d="M29.3848 8.98256C29.3848 8.21899 30.0327 7.6 30.832 7.6C31.6312 7.6 32.2791 8.21899 32.2791 8.98256V24.1907C32.2791 24.9543 31.6312 25.5733 30.832 25.5733C30.0327 25.5733 29.3848 24.9543 29.3848 24.1907V8.98256Z"
        fill="white" />
      <path d="M23.534 20.3294C22.9689 19.7894 22.9689 18.9141 23.534 18.3741C24.0992 17.8342 25.0155 17.8342 25.5806 18.3741L31.7205 24.2398C32.2856 24.7797 32.2856 25.6551 31.7205 26.1951C31.1553 26.735 30.239 26.735 29.6739 26.1951L23.534 20.3294Z"
        fill="white" />
      <path d="M37.9744 20.3294C38.5396 19.7894 38.5396 18.9141 37.9744 18.3741C37.4093 17.8342 36.493 17.8342 35.9278 18.3741L29.788 24.2398C29.2228 24.7797 29.2228 25.6551 29.788 26.1951C30.3532 26.735 31.2695 26.735 31.8346 26.1951L37.9744 20.3294Z"
        fill="white" />
      <path d="M15.4661 30.4C15.4661 29.3507 16.3217 28.5 17.3771 28.5H46.0424C47.0978 28.5 47.9534 29.3507 47.9534 30.4C47.9534 31.4493 47.0978 32.3 46.0424 32.3H17.3771C16.3217 32.3 15.4661 31.4493 15.4661 30.4Z"
        fill="white" />
      <path d="M17.3771 19C18.4325 19 19.2881 19.8507 19.2881 20.9V30.4C19.2881 31.4493 18.4325 32.3 17.3771 32.3C16.3217 32.3 15.4661 31.4493 15.4661 30.4V20.9C15.4661 19.8507 16.3217 19 17.3771 19Z"
        fill="white" />
      <path d="M46.0424 19C47.0978 19 47.9534 19.8507 47.9534 20.9V30.4C47.9534 31.4493 47.0978 32.3 46.0424 32.3C44.9869 32.3 44.1314 31.4493 44.1314 30.4V20.9C44.1314 19.8507 44.9869 19 46.0424 19Z"
        fill="white" />
    </g>
  </svg>
);

const SaveIcon = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_4853_21177)">
      <path d="M14.8964 1.75077L13.2321 0.10257C13.1643 0.0389059 13.075 0 12.9786 0H2.25C2.21429 0 2.18571 0 2.15 0C2.15 0 2.14643 0 2.14286 0H0.357143C0.160714 0 0 0.159161 0 0.35369V14.6463C0 14.8408 0.160714 15 0.357143 15H14.6429C14.8393 15 15 14.8408 15 14.6463V2.00189C15 1.90639 14.9607 1.81797 14.8964 1.75077ZM7.5 11.8486C6.11786 11.8486 5 10.7416 5 9.37279C5 8.00401 6.11786 6.89696 7.5 6.89696C8.88214 6.89696 10 8.00401 10 9.37279C10 10.7416 8.88214 11.8486 7.5 11.8486ZM11.4286 3.90474C11.4286 4.49187 10.95 4.96581 10.3571 4.96581H4.64286C4.05 4.96581 3.57143 4.49187 3.57143 3.90474V1.40769C3.57143 1.22377 3.72143 1.07522 3.90714 1.07522L7.91071 1.06814C8.07857 1.06814 8.21429 1.20255 8.21429 1.36878V3.47324C8.21429 3.70667 8.40357 3.89413 8.63929 3.89413H9.575C9.81071 3.89413 10 3.70667 10 3.47324V1.36171C10 1.19547 10.1357 1.06107 10.3036 1.06107H11.0929C11.2786 1.06107 11.4286 1.20962 11.4286 1.39354V3.90474Z"
        fill="#E1E5ED" />
    </g>
    <defs>
      <clipPath id="clip0_4853_21177">
        <rect width="15" height="15" fill="white" />
      </clipPath>
    </defs>
  </svg>
);
